import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGovtExpensesReport, getGovtExpensesSummary } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  CreditCard, 
  TrendingUp, 
  TrendingDown, 
  Calendar,
  Download,
  Filter,
  Receipt,
  FileText,
  DollarSign,
  BarChart3,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Banknote
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';
import SmartSearch from '@/components/SmartSearch';

// Column definitions for smart search
const expenseColumns = [
  { key: 'expense_number', label: 'Expense #', type: 'text' },
  { key: 'service_order_number', label: 'Order #', type: 'text' },
  { key: 'client_name', label: 'Client', type: 'text' },
  { key: 'amount', label: 'Govt Fee Paid', type: 'number' },
  { key: 'payments_received', label: 'Payments Received', type: 'number' },
  { key: 'profit_loss', label: 'Profit/Loss', type: 'number' },
  { key: 'expense_date', label: 'Date', type: 'date' },
  { key: 'invoice_number', label: 'Invoice #', type: 'text' },
];

// Quick filter presets
const quickFilters = [
  { label: 'Profitable', filters: { is_profitable: true } },
  { label: 'Loss Making', filters: { is_profitable: false } },
  { label: 'This Month', filters: { expense_date: 'thisMonth' } },
  { label: 'Unpaid', filters: { payments_received: '0' } },
];

export default function GovtExpensesReportPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Summary data
  const [summary, setSummary] = useState(null);
  
  // All expenses
  const [allExpenses, setAllExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  
  // Report data
  const [reportData, setReportData] = useState(null);
  
  // Date filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Expanded rows for payment details
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, reportRes] = await Promise.all([
        getGovtExpensesSummary().catch(() => ({ data: null })),
        getGovtExpensesReport({}).catch(() => ({ data: null }))
      ]);
      
      setSummary(summaryRes.data);
      setReportData(reportRes.data);
      setAllExpenses(reportRes.data?.expenses || []);
      setFilteredExpenses(reportRes.data?.expenses || []);
    } catch (error) {
      toast.error('Failed to load government expenses data');
    } finally {
      setLoading(false);
    }
  };

  const loadFilteredReport = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const res = await getGovtExpensesReport(params);
      setReportData(res.data);
      setAllExpenses(res.data?.expenses || []);
      setFilteredExpenses(res.data?.expenses || []);
    } catch (error) {
      toast.error('Failed to load filtered data');
    }
  };

  const toggleRowExpanded = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const exportToCsv = () => {
    const headers = [
      'Expense #', 'Date', 'Order #', 'Invoice #', 'Client', 
      'Govt Fee Paid', 'Payments Received', 'Profit/Loss', 'Status'
    ];
    const rows = filteredExpenses.map(exp => [
      exp.expense_number,
      exp.expense_date,
      exp.service_order_number || '',
      exp.invoice_number || '',
      exp.client_name || '',
      exp.amount,
      exp.payments_received || 0,
      exp.profit_loss || -exp.amount,
      (exp.profit_loss || 0) >= 0 ? 'Profit' : 'Loss'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `govt-expenses-profit-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Report exported to CSV');
  };

  const getProfitLossColor = (value) => {
    if (value > 0) return 'text-green-600';
    if (value < 0) return 'text-red-600';
    return 'text-muted-foreground';
  };

  const getProfitLossBadge = (value) => {
    if (value > 0) return <Badge className="bg-green-100 text-green-700">Profit</Badge>;
    if (value < 0) return <Badge className="bg-red-100 text-red-700">Loss</Badge>;
    return <Badge variant="secondary">Break Even</Badge>;
  };

  // Calculate totals from filtered data
  const totalGovtFees = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalPayments = filteredExpenses.reduce((sum, e) => sum + (e.payments_received || 0), 0);
  const totalProfit = filteredExpenses.reduce((sum, e) => sum + (e.profit_loss || -e.amount), 0);
  const profitableCount = filteredExpenses.filter(e => (e.profit_loss || 0) >= 0).length;

  return (
    <div className="space-y-6" data-testid="govt-expenses-report-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-primary" />
            Government Expenses & Profit Report
          </h1>
          <p className="text-muted-foreground">Track government fees paid, client payments, and profit/loss in real-time</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            Refresh
          </Button>
          <Button variant="outline" onClick={exportToCsv}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Govt Fees Paid</p>
                <p className="text-2xl font-bold text-red-600">-{formatCurrency(summary?.all_time?.govt_fees_paid || 0)}</p>
                <p className="text-xs text-muted-foreground">{summary?.all_time?.count || 0} transactions</p>
              </div>
              <div className="p-3 rounded-full bg-red-100">
                <CreditCard className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Payments Received</p>
                <p className="text-2xl font-bold text-green-600">+{formatCurrency(summary?.all_time?.payments_received || 0)}</p>
                <p className="text-xs text-muted-foreground">From client invoices</p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <Banknote className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit/Loss</p>
                <p className={`text-2xl font-bold ${getProfitLossColor(summary?.all_time?.profit_loss || 0)}`}>
                  {(summary?.all_time?.profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(summary?.all_time?.profit_loss || 0)}
                </p>
                <p className="text-xs text-muted-foreground">All time balance</p>
              </div>
              <div className={`p-3 rounded-full ${(summary?.all_time?.profit_loss || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                {(summary?.all_time?.profit_loss || 0) >= 0 ? (
                  <TrendingUp className="w-6 h-6 text-green-600" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This Month P/L</p>
                <p className={`text-2xl font-bold ${getProfitLossColor(summary?.this_month?.profit_loss || 0)}`}>
                  {(summary?.this_month?.profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(summary?.this_month?.profit_loss || 0)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Paid: {formatCurrency(summary?.this_month?.govt_fees_paid || 0)} | Received: {formatCurrency(summary?.this_month?.payments_received || 0)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="details">Detailed Tracking</TabsTrigger>
          <TabsTrigger value="by-month">Monthly Analysis</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Expenses with Profit */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Recent Govt Expenses & Profit Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Govt Fee</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                      <TableHead className="text-right">P/L</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(summary?.recent_expenses || []).slice(0, 8).map(exp => (
                      <TableRow key={exp.id}>
                        <TableCell>
                          <Button 
                            variant="link" 
                            className="p-0 h-auto font-mono text-xs"
                            onClick={() => navigate(`/service-orders/${exp.service_order_id}`)}
                          >
                            {exp.service_order_number || '-'}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium text-sm">{exp.client_name || '-'}</TableCell>
                        <TableCell className="text-right font-mono text-red-600 text-sm">
                          -{formatCurrency(exp.amount)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600 text-sm">
                          +{formatCurrency(exp.payments_received || 0)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono font-semibold ${getProfitLossColor(exp.profit_loss || -exp.amount)}`}>
                            {(exp.profit_loss || -exp.amount) >= 0 ? '+' : ''}{formatCurrency(exp.profit_loss || -exp.amount)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!summary?.recent_expenses || summary.recent_expenses.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No government expenses recorded yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Monthly Profit Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Monthly Profit Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Paid Out</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                      <TableHead className="text-right">Profit/Loss</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(reportData?.by_month || []).slice(0, 6).map(item => (
                      <TableRow key={item.month}>
                        <TableCell className="font-medium">{item.month}</TableCell>
                        <TableCell className="text-right font-mono text-red-600">
                          -{formatCurrency(item.govt_fees)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600">
                          +{formatCurrency(item.payments_received)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono font-semibold ${getProfitLossColor(item.profit)}`}>
                            {item.profit >= 0 ? '+' : ''}{formatCurrency(item.profit)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!reportData?.by_month || reportData.by_month.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Detailed Tracking Tab */}
        <TabsContent value="details" className="mt-6">
          <Card>
            <CardHeader>
              <div className="space-y-4">
                {/* Date Filter */}
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <Label>From Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  <div>
                    <Label>To Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  <Button onClick={loadFilteredReport} variant="outline">
                    <Filter className="w-4 h-4 mr-2" />
                    Apply Filter
                  </Button>
                </div>
                
                <SmartSearch
                  data={allExpenses}
                  columns={expenseColumns}
                  onFilter={setFilteredExpenses}
                  quickFilters={quickFilters}
                  placeholder="Search by expense #, order #, client, invoice..."
                />
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Order / Invoice</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-right">Govt Fee Paid</TableHead>
                    <TableHead className="text-right">Payments Received</TableHead>
                    <TableHead className="text-right">Profit/Loss</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map(exp => (
                    <React.Fragment key={exp.id}>
                      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleRowExpanded(exp.id)}>
                        <TableCell>
                          {exp.payment_details?.length > 0 ? (
                            expandedRows[exp.id] ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )
                          ) : null}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(exp.expense_date)}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <Button 
                              variant="link" 
                              className="p-0 h-auto font-mono text-xs"
                              onClick={(e) => { e.stopPropagation(); navigate(`/service-orders/${exp.service_order_id}`); }}
                            >
                              {exp.service_order_number}
                            </Button>
                            {exp.invoice_number && (
                              <div className="flex items-center gap-1">
                                <FileText className="w-3 h-3 text-muted-foreground" />
                                <Button 
                                  variant="link" 
                                  className="p-0 h-auto font-mono text-xs text-muted-foreground"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${exp.invoice_id}`); }}
                                >
                                  {exp.invoice_number}
                                </Button>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{exp.client_name || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <CreditCard className="w-3 h-3 text-muted-foreground" />
                            <span className="font-mono text-red-600">-{formatCurrency(exp.amount)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Banknote className="w-3 h-3 text-muted-foreground" />
                            <span className="font-mono text-green-600">+{formatCurrency(exp.payments_received || 0)}</span>
                          </div>
                          {exp.payment_count > 0 && (
                            <p className="text-xs text-muted-foreground">{exp.payment_count} payment(s)</p>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono font-bold ${getProfitLossColor(exp.profit_loss || -exp.amount)}`}>
                            {(exp.profit_loss || -exp.amount) >= 0 ? '+' : ''}{formatCurrency(exp.profit_loss || -exp.amount)}
                          </span>
                        </TableCell>
                        <TableCell>{getProfitLossBadge(exp.profit_loss || -exp.amount)}</TableCell>
                      </TableRow>
                      
                      {/* Expanded Payment Details */}
                      {expandedRows[exp.id] && exp.payment_details?.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-muted/30 p-0">
                            <div className="p-4">
                              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                <Receipt className="w-4 h-4" />
                                Payment Details
                              </h4>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Receipt #</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Method</TableHead>
                                    <TableHead>Reference</TableHead>
                                    <TableHead className="text-right">Amount</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {exp.payment_details.map((payment, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="font-mono text-xs">{payment.receipt_number}</TableCell>
                                      <TableCell>{formatDate(payment.payment_date)}</TableCell>
                                      <TableCell>
                                        <Badge variant="outline">{payment.payment_method}</Badge>
                                      </TableCell>
                                      <TableCell className="text-muted-foreground">{payment.reference_number || '-'}</TableCell>
                                      <TableCell className="text-right font-mono text-green-600">
                                        +{formatCurrency(payment.amount)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                  {filteredExpenses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No government expenses found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {/* Totals */}
              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Govt Fees Paid</p>
                    <p className="text-lg font-bold font-mono text-red-600">-{formatCurrency(totalGovtFees)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Payments Received</p>
                    <p className="text-lg font-bold font-mono text-green-600">+{formatCurrency(totalPayments)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Net Profit/Loss</p>
                    <p className={`text-lg font-bold font-mono ${getProfitLossColor(totalProfit)}`}>
                      {totalProfit >= 0 ? '+' : ''}{formatCurrency(totalProfit)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Profitable Orders</p>
                    <p className="text-lg font-bold">{profitableCount} / {filteredExpenses.length}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Analysis Tab */}
        <TabsContent value="by-month" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Government Expenses & Profit Analysis</CardTitle>
              <CardDescription>Comprehensive breakdown showing paid amounts, received payments, and profit/loss by month</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-center">Transactions</TableHead>
                    <TableHead className="text-right">Govt Fees Paid</TableHead>
                    <TableHead className="text-right">Payments Received</TableHead>
                    <TableHead className="text-right">Net Profit/Loss</TableHead>
                    <TableHead className="text-right">Avg P/L per Order</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(reportData?.by_month || []).map(item => (
                    <TableRow key={item.month}>
                      <TableCell className="font-medium">{item.month}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{item.count}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        -{formatCurrency(item.govt_fees)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        +{formatCurrency(item.payments_received)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`font-mono font-bold ${getProfitLossColor(item.profit)}`}>
                          {item.profit >= 0 ? '+' : ''}{formatCurrency(item.profit)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {item.count > 0 ? formatCurrency(item.profit / item.count) : '-'}
                      </TableCell>
                      <TableCell>{getProfitLossBadge(item.profit)}</TableCell>
                    </TableRow>
                  ))}
                  {(!reportData?.by_month || reportData.by_month.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No monthly data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {/* Grand Total */}
              {reportData?.by_month && reportData.by_month.length > 0 && (
                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Govt Fees Paid</p>
                      <p className="text-xl font-bold font-mono text-red-600">
                        -{formatCurrency(reportData.by_month.reduce((sum, m) => sum + m.govt_fees, 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Payments Received</p>
                      <p className="text-xl font-bold font-mono text-green-600">
                        +{formatCurrency(reportData.by_month.reduce((sum, m) => sum + m.payments_received, 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Grand Total Profit/Loss</p>
                      <p className={`text-xl font-bold font-mono ${getProfitLossColor(reportData.by_month.reduce((sum, m) => sum + m.profit, 0))}`}>
                        {reportData.by_month.reduce((sum, m) => sum + m.profit, 0) >= 0 ? '+' : ''}
                        {formatCurrency(reportData.by_month.reduce((sum, m) => sum + m.profit, 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Transactions</p>
                      <p className="text-xl font-bold">
                        {reportData.by_month.reduce((sum, m) => sum + m.count, 0)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
